<template>
  <div>
    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <button 
        class="mr-2 p-2 text-white bg-teal-800 hover:bg-teal-900 rounded transition-colors" 
        title="Volver atrás"
        @click="back()"
      >
        <i class="fa fa-arrow-left" />
      </button>
      <h1 class="text-3xl font-bold">Editar venta #{{ order.number_order }}</h1>
    </div>

    <Head title="Editar venta" />

    <!-- Wizard Steps Header -->
    <div class="flex items-center justify-center mb-8">
      <div v-for="step in steps" :key="step.id" class="flex items-center">
        <div :class="['w-8 h-8 flex items-center justify-center rounded-full', currentStep === step.id ? 'bg-teal-700 text-white' : 'bg-gray-200 text-gray-600']">
          {{ step.id }}
        </div>
        <span class="ml-2 mr-4 font-semibold" :class="currentStep === step.id ? 'text-teal-700' : 'text-gray-500'">{{ step.label }}</span>
        <span v-if="step.id < steps.length" class="w-8 h-1 bg-gray-300 mx-1 rounded" />
      </div>
    </div>

    <!-- Paso 1: Cliente -->
    <div v-if="currentStep === 1">
      <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div class="p-6">
          <find-or-create-client @addClient="takeClient" />
          <div v-if="showClientCreate" class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <text-input 
              v-model="form.client.name"
              label="Nombre y Apellido"
              placeholder="Ingrese el nombre completo"
            />
            <text-input
              v-model="form.client.identification" 
              label="Alias o Cédula"
              placeholder="Ingrese identificación"
            />
            <text-input
              v-model="form.client.phone"
              label="Teléfono"
              placeholder="Ingrese número de contacto" 
            />
            <text-input
              v-model="form.client.address"
              label="Dirección"
              placeholder="Ingrese dirección completa"
            />
            <text-input
              v-model="form.client.balance"
              label="Monto adeudado" 
              disabled
            />
            <div>
              <label class="block text-gray-700 mb-2">Tipo de cliente</label>
              <select 
                v-model="form.client.typeClient"
                class="form-select w-full rounded-lg border-gray-300"
              >
                <option value="">Seleccionar tipo</option>
                <option value="freelancer">Freelancer</option>
                <option value="spot">SPOT</option>
                <option value="a">A</option>
                <option value="b">B</option>
                <option value="c">C</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-end">
        <button 
          class="btn-blue px-8 py-3 text-lg"
          @click="nextStep"
        >
          Siguiente <i class="fa fa-arrow-right ml-2" />
        </button>
      </div>
    </div>

    <!-- Paso 2: Productos y Descuento -->
    <div v-if="currentStep === 2">
      <!-- Visual Header -->
      <div class="mb-6 text-center">
        <h2 class="text-2xl font-bold text-teal-800 mb-1 flex items-center justify-center gap-2">
          <i class="fa fa-shopping-cart text-teal-500" /> Selecciona productos, servicios o aplica descuento
        </h2>
        <p class="text-gray-500">Arma el carrito, agrega servicios rápidos y ajusta el descuento para tu cliente.</p>
      </div>

      <!-- Main Actions -->
      <div class="flex flex-col sm:flex-row gap-4 mb-4">
        <button 
          class="flex-1 btn-blue flex items-center justify-center gap-2 py-3 text-lg shadow-md"
          title="Ver lista de productos"
          @click="listProducts = !listProducts"
        >
          <i class="fa fa-list" />
          Lista de productos
        </button>
        <button 
          class="flex-1 btn-blue flex items-center justify-center gap-2 py-3 text-lg shadow-md"
          title="Agregar servicio o producto rápido"
          @click="addQuickProduct()"
        >
          <i class="fa fa-plus" />
          Servicio o producto rápido
        </button>
      </div>

      <!-- Search Bar -->
      <div class="mb-6">
        <text-input
          v-model="form.search.terms"
          placeholder="Buscar productos..."
          class="w-full text-lg border-2 border-teal-200 rounded-lg shadow-sm"
          input-class="focus:ring-teal-500 focus:border-teal-500"
        >
          <template #append>
            <i class="fa fa-search text-teal-400" />
          </template>
        </text-input>
      </div>

      <!-- Floating Product List -->
      <transition name="fade">
        <div v-if="listProducts" class="mb-8">
          <div class="bg-white border border-teal-100 rounded-2xl shadow-xl max-h-80 overflow-y-auto p-4">
            <table class="w-full text-base">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th class="p-3 text-left">Código</th>
                  <th class="p-3 text-left">Producto</th>
                  <th class="p-3 text-center">Disponibles</th>
                  <th class="p-3 text-right">Precio</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="product in products.data" 
                  :key="product.id"
                  class="hover:bg-teal-50 cursor-pointer border-t transition"
                  @click="selectProduct(product)"
                >
                  <td class="p-3 font-mono">{{ product.code }}</td>
                  <td class="p-3">{{ product.name }}</td>
                  <td class="p-3 text-center">{{ product.type_product == 'product' ? product.inventory.quantity : 'N/A' }}</td>
                  <td class="p-3 text-right text-teal-700 font-semibold">${{ product.prices[0].price }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </transition>

      <!-- Cart Table -->
      <div class="bg-white rounded-xl shadow-md border border-teal-100 mb-8 overflow-x-auto">
        <table class="w-full text-base">
          <thead class="bg-teal-50">
            <tr>
              <th class="p-3 text-left">Código</th>
              <th class="p-3 text-left">Producto</th>
              <th class="p-3 text-center">Disponible</th>
              <th class="p-3 text-center">Cantidad</th>
              <th class="p-3 text-right">Precio</th>
              <th class="p-3 text-right">Total</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in form.products" :key="product.id" class="border-t hover:bg-gray-50 transition">
              <td class="p-3">
                <text-input 
                  v-if="product.quick"
                  v-model="form.products[index].code"
                  placeholder="Código"
                  class="w-full"
                  input-class="focus:ring-teal-500 focus:border-teal-500"
                />
                <span v-else class="font-mono">{{ product.code }}</span>
              </td>
              <td class="p-3 align-top">
                <div>
                  <text-input
                    v-if="product.quick"
                    v-model="form.products[index].name"
                    placeholder="Nombre"
                    class="w-full mb-1"
                    input-class="focus:ring-teal-500 focus:border-teal-500"
                  />
                  <span v-else>{{ product.name }}</span>
                  <!-- Description for services -->
                  <div v-if="product.type_product === 'service' || product.quick">
                    <textarea
                      v-model="form.products[index].description"
                      rows="2"
                      class="form-textarea w-full rounded-lg border-gray-300 mt-2 text-sm focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Describe el servicio realizado, detalles, condiciones, etc."
                    />
                  </div>
                  <div v-else-if="product.description" class="text-xs text-gray-500 mt-1">{{ product.description }}</div>
                </div>
              </td>
              <td class="p-3 text-center">{{ product.stock }}</td>
              <td class="p-3">
                <text-input
                  v-model="form.products[index].quantity"
                  type="number"
                  :max="product.stock !== 'N/A' ? product.stock : null"
                  class="w-20 text-center"
                  input-class="focus:ring-teal-500 focus:border-teal-500"
                />
              </td>
              <td class="p-3">
                <text-input
                  v-model="form.products[index].price"
                  type="number"
                  :disabled="!product.quick && auth.user.role == 'sales'"
                  class="w-24 text-right"
                  input-class="focus:ring-teal-500 focus:border-teal-500"
                />
              </td>
              <td class="p-3 text-right font-semibold text-teal-700">
                ${{ (form.products[index].price * product.quantity).toFixed(2) }}
              </td>
              <td class="p-3 text-center">
                <button
                  class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors shadow"
                  title="Eliminar producto"
                  @click="removeProduct(product)"
                >
                  <i class="fa fa-trash" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totals and Discount Summary -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <!-- Totals Summary -->
        <div class="bg-teal-50 border border-teal-100 rounded-xl p-6 shadow flex flex-col gap-2 mb-2 md:mb-0">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-teal-700">Subtotal:</span>
            <span class="font-medium">${{ getSubtotal() }}</span>
          </div>
          <div v-if="getDiscountAmount() > 0" class="flex justify-between items-center text-red-600">
            <span>Descuento <span v-if="form.discount.type === 'percentage'">({{ form.discount.value }}%)</span>:</span>
            <span>- ${{ getDiscountAmount() }}</span>
          </div>
          <div class="flex justify-between items-center font-bold text-xl mt-2">
            <span>Total:</span>
            <span class="text-teal-800">${{ getTotal() }}</span>
          </div>
          <div class="flex items-center gap-2 mt-2">
            <span class="text-sm text-gray-500">Tasa de cambio:</span>
            <text-input
              v-model="form.change"
              type="number"
              class="w-24 text-right"
              input-class="focus:ring-teal-500 focus:border-teal-500"
            />
            <span class="text-sm text-gray-500">Bs. {{ (getTotal() * form.change).toFixed(2) }}</span>
          </div>
        </div>
        <!-- Discount Controls -->
        <div class="bg-white border border-teal-100 rounded-xl p-6 shadow flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <label class="font-semibold text-teal-700">Descuento:</label>
            <select v-model="form.discount.type" class="form-select rounded-lg border-gray-300">
              <option value="none">Sin descuento</option>
              <option value="percentage">Porcentaje (%)</option>
              <option value="amount">Monto ($)</option>
            </select>
            <text-input
              v-if="form.discount.type !== 'none'"
              v-model="form.discount.value"
              type="number"
              class="w-24 text-right"
              input-class="focus:ring-teal-500 focus:border-teal-500"
              :placeholder="form.discount.type === 'percentage' ? '0%' : '$0.00'"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-between mt-4">
        <button class="btn-blue px-8 py-3 text-lg" @click="prevStep">
          <i class="fa fa-arrow-left mr-2" /> Anterior
        </button>
        <button 
          class="btn-blue px-8 py-3 text-lg"
          @click="nextStep"
        >
          Siguiente <i class="fa fa-arrow-right ml-2" />
        </button>
      </div>
      <div v-if="stepWarning && currentStep === 2" class="text-red-600 text-sm mt-2 text-center">
        {{ stepWarning }}
      </div>
    </div>
    <!-- End Paso 2 -->
    <div v-if="currentStep === 3">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <!-- Cliente -->
        <div class="flex flex-col gap-4 bg-white rounded-xl shadow p-4 border border-teal-100">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-user-circle text-2xl text-teal-500" />
            <span class="font-semibold text-teal-800 text-lg">Cliente</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-id-badge text-gray-400" />
            <span class="font-semibold">{{ form.client.name }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-address-card text-gray-400" />
            <span class="text-gray-800 text-sm">{{ form.client.identification }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-envelope text-gray-400" />
            <span class="text-gray-800 text-sm">{{ form.client.email || '-' }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-phone text-gray-400" />
            <span class="text-gray-800 text-sm">{{ form.client.phone || '-' }}</span>
          </div>
        </div>
        <!-- Productos y servicios -->
        <div class="col-span-1 md:col-span-2 flex flex-col gap-4 bg-white rounded-xl shadow p-4 border border-teal-100">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-boxes text-2xl text-teal-500" />
            <span class="font-semibold text-teal-800 text-lg">Productos y Servicios</span>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="p-2 text-left">Producto/Servicio</th>
                  <th class="p-2 text-left">Descripción</th>
                  <th class="p-2 text-center">Cantidad</th>
                  <th class="p-2 text-right">Precio</th>
                  <th class="p-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, idx) in form.products" :key="idx" class="border-b">
                  <td class="p-2">{{ product.name }}</td>
                  <td class="p-2 text-xs text-gray-600">{{ product.description || '-' }}</td>
                  <td class="p-2 text-center">{{ product.quantity }}</td>
                  <td class="p-2 text-right">${{ Number(product.price).toFixed(2) }}</td>
                  <td class="p-2 text-right">${{ (product.price * product.quantity).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 justify-between mt-4">
            <div class="flex items-center gap-2">
              <i class="fa fa-tag text-gray-400" />
              <span class="font-semibold">Descuento:</span>
              <span v-if="form.discount && form.discount.value">{{ form.discount.type === 'amount' ? ('$' + Number(form.discount.value).toFixed(2)) : (Number(form.discount.value) + '%') }}</span>
              <span v-else>-</span>
            </div>
            <div class="flex items-center gap-2">
              <i class="fa fa-coins text-gray-400" />
              <span class="font-semibold">Total estimado:</span>
              <span class="text-teal-700 text-lg font-bold">
                ${{ getTotal() }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <!-- Observaciones y controles -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div class="p-6 space-y-6">
          <text-input
            v-model="form.date"
            label="Fecha y hora"
            type="datetime-local"
            required
          />
          <text-input
            v-model="form.change"
            label="Tasa de cambio"
            type="number"
            step="0.01"
          />
          <text-area-input
            v-model="form.comentary"
            label="Observaciones"
            placeholder="Ingrese observaciones importantes"
            required
          />
        </div>
      </div>
      <div class="flex justify-between">
        <button class="btn-blue px-8 py-3 text-lg" @click="prevStep">
          <i class="fa fa-arrow-left mr-2" /> Anterior
        </button>
        <button 
          class="btn-blue text-lg px-8 py-3"
          @click="updateOrder"
        >
          <i class="fa fa-check mr-2" />
          Guardar Cambios
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '../../Shared/Layout.vue'
import TextInput from '@/Shared/TextInput'
import pickBy from 'lodash/pickBy'
import { Head } from '@inertiajs/inertia-vue3'
import SelectInput from '@/Shared/SelectInput.vue'
import FindOrCreateClient from '../../Shared/FindOrCreateClient.vue'
import TextAreaInput from '@/Shared/TextAreaInput'

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
    FindOrCreateClient,
    TextAreaInput,
  },

  layout: Layout,

  props: {
    order: Object,
    products: Object,
    client: Object,
    brands: Object,
    parts: Object,
    models: Object,
    deposits: Object,
    regions: Object,
    change: Number,
    auth: Object,
  },

  data() {
    return {
      currentStep: 1,
      steps: [
        { id: 1, label: 'Cliente' },
        { id: 2, label: 'Productos' },
        { id: 3, label: 'Resumen' },
      ],
      getQuantity(inventory) {
        return inventory.reduce((total, inv) => total + inv.quantity, 0)
      },
      selected: null,
      options: ['list', 'of', 'options'],
      form: this.$inertia.form({
        date: this.order.date,
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
        isCredit: this.order.isCredit,
        number_order: this.order.number_order,
        client: {
          name: this.order.client.name,
          email: this.order.client.email,
          cedula: this.order.client.identification,
          telefono: this.order.client.phone,
          address: this.order.client.address,
          identification: this.order.client.identification,
          balance: this.order.client.balance,
          typeClient: this.order.client.typeClient,
        },
        discount: {
          type: 'percentage',
          value: this.order.discount || 0,
        },
        comentary: this.order.comentary,
        products: this.order.products.map(product => ({
          ...product,
          stock: product.quantity,
          quick: false,
        })),
        method: this.order.method,
        referencePay: this.order.referencePay,
        change: this.order.change,
        estimate: this.order.estimate,
        order_buy: this.order.order_buy,
      }),
      listProducts: false,
      showClientCreate: false,
      textSearch: 'Buscar cliente',
    }
  },

  watch: {
    'form.search.terms': 'onSearchProductsChange',
    'form.search.deposit': 'onSearchProductsChange', 
    'form.search.brand_id': 'onSearchProductsChange',
    'form.search.part_id': 'onSearchProductsChange',
    'form.search.truck_model_id': 'onSearchProductsChange',
    'form.search.region_id': 'onSearchProductsChange',
  },

  mounted() {
    this.takeClient(this.order.client)
  },

  methods: {
    takeClient(client) {
      this.form.client = client
    },

    getDeposits(deposits) {
      return deposits
    },

    getSubtotal() {
      return this.form.products.reduce((total, product) => {
        return total + (parseFloat(product.price) * parseFloat(product.quantity))
      }, 0).toFixed(2)
    },

    getDiscountAmount() {
      const subtotal = parseFloat(this.getSubtotal())
      if (this.form.discount.type === 'percentage') {
        return ((subtotal * parseFloat(this.form.discount.value)) / 100).toFixed(2)
      } else {
        return Math.min(parseFloat(this.form.discount.value), subtotal).toFixed(2)
      }
    },

    getTotal() {
      const subtotal = parseFloat(this.getSubtotal())
      const discount = parseFloat(this.getDiscountAmount())
      return (subtotal - discount).toFixed(2)
    },

    back() {
      window.history.back()
    },

    addQuickProduct() {
      this.form.products.push({
        id: 'quick',
        code: '',
        name: '',
        deposit: '',
        quantity: 1,
        price: 0,
        quick: true,
      })
    },

    getProductStock(inventory, deposito) {
      const found = inventory.find(inv => inv.deposit.id == deposito)
      return found ? found.quantity : ''
    },

    selectProduct(product) {
      if (product.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const exists = this.form.products.some(p => p.id === product.id)
      
      if (!exists) {
        this.form.products.push({
          ...product,
          stock: product.quantity,
          quantity: 1,
          price: product?.prices[0]['price'],
          quick: false,
        })
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.form.products = this.form.products.filter(p => p.id !== product.id)
      }
    },

    findClient() {
      this.$inertia.get('/orders/create', pickBy(this.form), { preserveState: true })
      this.textSearch = 'Buscando...'

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient,
            companyName: this.client.companyName,
          }
        } else {
          this.textSearch = 'Buscar cliente'
          alert('No se encontró el cliente. Puede crear uno nuevo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },

    updateOrder() {
      this.form.put(`/orders/${this.order.id}`)
    },

    onSearchProductsChange() {
      this.$inertia.get('/orders/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        },
      )
    },

    onSelectedProduct(consultant) {
      console.log(consultant)
    },

    updateSelected(newSelected) {
      this.selectProduct = newSelected
    },
    nextStep() {
      if (this.currentStep < this.steps.length) this.currentStep++
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--
    },
  },
}
</script>